import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    Paper,
    Box,
    IconButton,
    InputAdornment,
} from '@mui/material';
import { AddOutlined, Calculate, DeleteOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Formik, Form, FieldArray, FormikProps } from 'formik';

import { useAppDispatch, useAppSelector } from 'src/redux-app-hooks';
import {
    FormFieldDto,
    FormFieldItemType,
    FormFieldType,
    FormItemKind,
    FormItemDto,
    DynamicSectionDto,
    RepeatType,
    VisibilityRuleDto,
    VisibilityMode,
    LogicOperator,
} from '../document-generation.dto';
import {
    getPrefilledForm,
    loadDependencyGraph,
    setDynamicOptions,
    setFormValues,
    setImagesForField,
    submitForm,
} from './document-generation-new-document.slice';
import { admiDataCatalogService } from '../admi-data-catalog.service';
import ImageSelector from 'src/common/components/ImageSelectorV2';
import { AsyncAutocompleteDropdown } from './AsyncAutocompleteDropdown';
import { mapFormSchemaToYupSchema } from './formSchemaToYup';
import { NotificationSeverity, showSnackbar } from 'src/features/notifications/notifications.slice';

function DynamicForm() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const {
        prefilledForm,
        selectedTemplate,
        formValues,
        imageFieldMap,
        dynamicOptions,
        selectedBuildings,
        dependencyGraph,
    } = useAppSelector((state) => state.documentGenerationNewDocument);

    const formikRef = useRef<FormikProps<Record<string, unknown>>>(null);

    useEffect(() => {
        if (selectedTemplate?.id) {
            dispatch(getPrefilledForm());
        }
    }, [dispatch, selectedTemplate]);

    useEffect(() => {
        if (!prefilledForm) return;
        const keys = prefilledForm.formSchema
            .filter(
                // Only consider computed fields
                (item) => item.kind === FormItemKind.FIELD && (item as FormFieldDto).type === FormFieldType.COMPUTED,
            )
            .map((item) => item.key);
        dispatch(loadDependencyGraph(keys));
    }, [dispatch, prefilledForm]);

    // Maps each dependency key to the computed field keys that depend on it
    const reverseIndex = useMemo<Record<string, string[]>>(() => {
        if (!dependencyGraph) return {};
        const index: Record<string, string[]> = {};
        Object.entries(dependencyGraph).forEach(([computedKey, deps]) => {
            deps.forEach((dep) => {
                if (!index[dep]) index[dep] = [];
                index[dep].push(computedKey);
            });
        });
        return index;
    }, [dependencyGraph]);

    const triggerComputedFieldsUpdate = useCallback(
        (changedKey: string) => {
            const affected = reverseIndex[changedKey];
            console.debug('triggerComputedFieldsUpdate', { changedKey, affected });
            if (!affected?.length || !formikRef.current || !dependencyGraph) return;

            const currentValues = formikRef.current.values;
            const changedDependencies: Record<string, unknown> = {};
            for (const computedKey of affected) {
                for (const dep of dependencyGraph[computedKey] ?? []) {
                    changedDependencies[dep] = currentValues[dep];
                }
            }

            admiDataCatalogService
                .resolveFields(affected, changedDependencies, false)
                .then((result) => {
                    console.debug('Computed fields update', { changedKey, changedDependencies, result });

                    Object.entries(result.values).forEach(([k, v]) => formikRef.current?.setFieldValue(k, v));
                })
                .catch(() => {
                    dispatch(
                        showSnackbar({
                            message: t('Could not compute dependent fields (changed field: {{field}})', {
                                field: changedKey,
                            }),
                            severity: NotificationSeverity.Error,
                        }),
                    );
                });
        },
        [reverseIndex, dependencyGraph, dispatch, t],
    );

    const processFormItem = (item: FormFieldDto | DynamicSectionDto, values: any): [string, any] => {
        if (item.kind === FormItemKind.FIELD) {
            const field = item as FormFieldDto;
            const val = values[field.key];

            if (field.type === FormFieldType.LIST) {
                const safeArray = Array.isArray(val) ? val : val == null ? [] : [val];
                return [field.key, safeArray];
            }

            if (field.type === FormFieldType.DATE && val && typeof val === 'string') {
                return [field.key, val ? dayjs(val as string) : null];
            }

            if (field.type === FormFieldType.CHECKBOX && val === undefined) {
                return [field.key, false];
            }

            // Attachments are handled differently as they're not pre-selected,
            // but only shown for selection. The show logic is part of the rendering.
            if (field.type === FormFieldType.ATTACHMENT) {
                return [field.key, undefined];
            }

            return [field.key, val];
        } else if (item.kind === FormItemKind.SECTION) {
            const section = item as DynamicSectionDto;
            const sectionValue = values[section.key];

            if (section.repeat.type === RepeatType.REPEATING) {
                const arrayValue = Array.isArray(sectionValue) ? sectionValue : [];
                const processedArray = arrayValue.map((instanceValue) => {
                    const processed: any = {};
                    section.items.forEach((subItem) => {
                        const [subKey, subValue] = processFormItem(
                            subItem as FormFieldDto | DynamicSectionDto,
                            instanceValue || {},
                        );
                        processed[subKey] = subValue;
                    });
                    return processed;
                });

                if (processedArray.length === 0 && section.repeat.initial) {
                    for (let i = 0; i < section.repeat.initial; i++) {
                        const newInstance: any = {};
                        section.items.forEach((subItem) => {
                            const [subKey, subValue] = processFormItem(subItem as FormFieldDto | DynamicSectionDto, {});
                            newInstance[subKey] = subValue;
                        });
                        processedArray.push(newInstance);
                    }
                }

                return [section.key, processedArray];
            } else {
                const processed: any = {};
                section.items.forEach((subItem) => {
                    const [subKey, subValue] = processFormItem(
                        subItem as FormFieldDto | DynamicSectionDto,
                        sectionValue || {},
                    );
                    processed[subKey] = subValue;
                });
                return [section.key, processed];
            }
        }
        return ['', undefined];
    };

    const initialValues = useMemo(() => {
        if (!prefilledForm || !prefilledForm.values) return {};

        return {
            ...prefilledForm.values,
            ...Object.fromEntries(
                prefilledForm.formSchema.map((item) =>
                    processFormItem(item as FormFieldDto | DynamicSectionDto, prefilledForm.values),
                ),
            ),
        };
    }, [prefilledForm]);

    if (!prefilledForm || !prefilledForm.values) return null;

    const evaluateCondition = (condition: any, formValues: any): boolean => {
        const { whenPath, op, value } = condition;
        const fieldValue = whenPath.split('.').reduce((obj: any, key: string) => obj?.[key], formValues);

        switch (op) {
            case LogicOperator.IS_EXACTLY:
                return fieldValue === value;
            case LogicOperator.IS_NOT:
                return fieldValue !== value;
            case LogicOperator.IS_EMPTY:
                return (
                    fieldValue == null || fieldValue === '' || (Array.isArray(fieldValue) && fieldValue.length === 0)
                );
            case LogicOperator.IS_NOT_EMPTY:
                return fieldValue != null && fieldValue !== '' && (!Array.isArray(fieldValue) || fieldValue.length > 0);
            case LogicOperator.GT:
                return Number(fieldValue) > Number(value);
            case LogicOperator.GTE:
                return Number(fieldValue) >= Number(value);
            case LogicOperator.LT:
                return Number(fieldValue) < Number(value);
            case LogicOperator.LTE:
                return Number(fieldValue) <= Number(value);
            case LogicOperator.INCLUDES:
                if (Array.isArray(fieldValue)) {
                    return fieldValue.includes(value);
                }
                return String(fieldValue).includes(String(value));
            default:
                return true;
        }
    };

    const evaluateVisibility = (visibleWhen: VisibilityRuleDto | null | undefined, formValues: any): boolean => {
        if (!visibleWhen) return true;

        const { mode, conditions } = visibleWhen;

        if (mode === VisibilityMode.ALL) {
            return conditions.every((condition) => evaluateCondition(condition, formValues));
        } else {
            return conditions.some((condition) => evaluateCondition(condition, formValues));
        }
    };

    const renderField = (
        field: FormFieldDto,
        value: any,
        setFieldValue: (value: any) => void,
        formikValues: any,
        errors: any,
        touched: any,
        fieldPath?: string,
        formikSetFieldValue?: (key: string, val: any) => void,
        onBlur?: () => void,
    ) => {
        const key = fieldPath || field.key;
        const config = field.config;
        const label = field.label;
        const fieldError = errors?.[field.key];
        const fieldTouched = touched?.[field.key];

        switch (field.type) {
            case FormFieldType.TEXT:
                return (
                    <TextField
                        name={key}
                        value={value || ''}
                        onChange={(e) => setFieldValue(e.target.value)}
                        onBlur={onBlur}
                        label={label}
                        size="small"
                        variant="outlined"
                        fullWidth
                        required={config.required}
                        placeholder={config.placeholder || ''}
                        error={Boolean(fieldError && fieldTouched)}
                        helperText={fieldTouched && fieldError ? String(fieldError) : ''}
                        slotProps={{
                            inputLabel: {
                                shrink: true,
                            },
                        }}
                    />
                );

            case FormFieldType.NUMBER:
                return (
                    <TextField
                        name={key}
                        value={value ?? ''}
                        onChange={(e) => {
                            const numValue = e.target.value === '' ? null : Number(e.target.value);
                            setFieldValue(numValue);
                        }}
                        onBlur={onBlur}
                        label={label}
                        size="small"
                        variant="outlined"
                        fullWidth
                        type="number"
                        required={config.required}
                        placeholder={config.placeholder || ''}
                        error={Boolean(fieldError && fieldTouched)}
                        helperText={fieldTouched && fieldError ? String(fieldError) : ''}
                    />
                );

            case FormFieldType.DATE:
                return (
                    <DatePicker
                        label={label}
                        value={value ? dayjs(value) : null}
                        onChange={(date) => setFieldValue(date?.isValid() ? date.format() : null)}
                        onClose={onBlur}
                        slotProps={{
                            textField: {
                                size: 'small',
                                fullWidth: true,
                                required: config.required,
                            },
                        }}
                    />
                );

            case FormFieldType.DROPDOWN: {
                if (config.dynamicOptionsKey) {
                    const dependencies =
                        prefilledForm.formMetadata?.dynamicOptionDependencies?.[config.dynamicOptionsKey] ?? [];

                    const dependencyValues = {
                        ...Object.fromEntries(dependencies.map((depKey) => [depKey, formikValues[depKey]])),
                        ...(dependencies.includes('buildingId') && selectedBuildings && selectedBuildings[0]
                            ? { buildingId: selectedBuildings[0].id }
                            : {}),
                    };
                    return (
                        <AsyncAutocompleteDropdown
                            fieldKey={key}
                            label={field.label}
                            required={config.required}
                            value={value}
                            onChange={(val) => setFieldValue(val)}
                            dynamicKey={config.dynamicOptionsKey}
                            placeholder={config.placeholder || ''}
                            options={dynamicOptions[config.dynamicOptionsKey] || []}
                            onOptionsChange={(options) => {
                                const optionsKey = config.dynamicOptionsKey!;
                                dispatch(setDynamicOptions({ key: optionsKey, options: options }));
                            }}
                            dependencies={dependencyValues}
                        />
                    );
                }

                // Find the option that matches the value, case-insensitive
                const originalOptions = config.options || [];
                const foundOption = originalOptions.find(
                    (opt) => opt.toLocaleUpperCase() === value?.toString()?.toLocaleUpperCase(),
                );
                const safeValue = foundOption ?? '';

                return (
                    <FormControl fullWidth required={config.required}>
                        <InputLabel>{label}</InputLabel>
                        <Select
                            size="small"
                            label={label}
                            value={safeValue}
                            onChange={(e) => {
                                setFieldValue(e.target.value);
                                setTimeout(() => onBlur?.(), 0); // Ensure onBlur is called after the value is updated
                            }}
                        >
                            {originalOptions.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );
            }

            case FormFieldType.CHECKBOX:
                return (
                    <FormControlLabel
                        control={
                            <Checkbox size="small" checked={value} onChange={(e) => setFieldValue(e.target.checked)} />
                        }
                        label={field.label}
                    />
                );

            case FormFieldType.LIST: {
                const isAttachmentList = field.typeItem === FormFieldItemType.ATTACHMENT;
                // ✅ Special case: LIST<ATTACHMENT> → render as single field with multiple selection
                if (isAttachmentList) {
                    return renderImageSelector({
                        field,
                        value,
                        multiple: true,
                        formikSetFieldValue: formikSetFieldValue!,
                        fieldPath,
                    });
                }
                return (
                    <FieldArray name={key}>
                        {({ push }) => (
                            <Stack spacing={2}>
                                <Typography variant="subtitle1">{label}</Typography>
                                {(Array.isArray(value) ? value : []).map((item: any, i: number) => (
                                    <React.Fragment key={i}>
                                        {renderField(
                                            {
                                                ...field,
                                                key: `${key}[${i}]`,
                                                type: field.typeItem as unknown as FormFieldType,
                                            },
                                            item,
                                            (val) => {
                                                const newList = [...value];
                                                newList[i] = val;
                                                setFieldValue(newList);
                                            },
                                            formikValues,
                                            null,
                                            null,
                                        )}
                                    </React.Fragment>
                                ))}
                                <Button variant="outlined" onClick={() => push('')}>
                                    <AddOutlined /> {t('Add')}
                                </Button>
                            </Stack>
                        )}
                    </FieldArray>
                );
            }

            case FormFieldType.ATTACHMENT:
                return renderImageSelector({
                    field,
                    value,
                    multiple: false,
                    formikSetFieldValue: formikSetFieldValue!,
                    fieldPath,
                });

            case FormFieldType.COMPUTED:
                return (
                    <TextField
                        name={key}
                        value={value || ''}
                        label={label}
                        size="small"
                        variant="filled"
                        fullWidth
                        required={config.required}
                        placeholder={config.placeholder || ''}
                        slotProps={{
                            inputLabel: {
                                shrink: true,
                            },
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Calculate />
                                    </InputAdornment>
                                ),
                                readOnly: true,
                            },
                        }}
                    />
                );

            default:
                return null;
        }
    };

    const renderImageSelector = ({
        field,
        value,
        multiple,
        formikSetFieldValue,
        fieldPath,
    }: {
        field: FormFieldDto;
        value: any;
        multiple: boolean;
        formikSetFieldValue: (key: string, val: any) => void;
        fieldPath?: string;
    }) => {
        const key = fieldPath || field.key;
        const config = field.config;
        const urls = Array.isArray(value) ? value : typeof value === 'string' ? [value] : [];

        return (
            <Stack spacing={1}>
                <Typography>
                    {field.label} {config.required ? '*' : ''}
                </Typography>
                <ImageSelector
                    name={key} // full path
                    imageUrls={urls} // remote urls (if any)
                    images={imageFieldMap[key] || []} // available images
                    onImagesChange={(imgs) => dispatch(setImagesForField({ field: key, images: imgs }))}
                    formik={{ setFieldValue: formikSetFieldValue }} // only setter
                    required={!!config.required}
                    multiple={multiple}
                    value={value} // ← pass the field’s current value
                />
            </Stack>
        );
    };

    const renderSection = (
        section: DynamicSectionDto,
        values: any,
        setFieldValue: (field: string, value: any) => void,
        errors: any,
        touched: any,
        allFormValues?: any,
    ) => {
        const sectionKey = section.key;
        const isRepeating = section.repeat.type === RepeatType.REPEATING;
        const formValuesForVisibility = allFormValues || values;

        if (!evaluateVisibility(section.visibleWhen, formValuesForVisibility)) {
            return null;
        }

        if (!isRepeating) {
            const sectionValues = values[sectionKey] || {};
            const sectionErrors = errors?.[sectionKey];
            const sectionTouched = touched?.[sectionKey];

            return (
                <Paper elevation={1} sx={{ p: 2, my: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {section.label}
                    </Typography>
                    <Stack spacing={2}>
                        {section.items.map((item) => (
                            <React.Fragment key={item.key}>
                                {renderFormItem(
                                    item as FormFieldDto | DynamicSectionDto,
                                    sectionValues,
                                    (field, val) => setFieldValue(`${sectionKey}.${field}`, val),
                                    sectionErrors,
                                    sectionTouched,
                                    formValuesForVisibility,
                                    sectionKey,
                                )}
                            </React.Fragment>
                        ))}
                    </Stack>
                </Paper>
            );
        }

        const repeatConfig = section.repeat;
        const sectionValues = values[sectionKey] || [];
        const minItems = repeatConfig.min || 0;
        const maxItems = repeatConfig.max || Number.MAX_SAFE_INTEGER;

        return (
            <Paper elevation={1} sx={{ p: 2, my: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {section.label}
                </Typography>
                <FieldArray name={sectionKey}>
                    {({ push, remove }) => (
                        <Stack spacing={2}>
                            {sectionValues.map((sectionInstance: any, index: number) => (
                                <Paper
                                    key={index}
                                    variant="outlined"
                                    sx={{ p: 2, position: 'relative', borderLeft: 3, borderColor: 'primary.main' }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                                            {section.label} #{index + 1}
                                        </Typography>
                                        {sectionValues.length > minItems && (
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => remove(index)}
                                                aria-label={t('Remove')}
                                            >
                                                <DeleteOutlined fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                    <Stack spacing={2}>
                                        {section.items.map((item) => {
                                            const itemValue = sectionInstance[item.key];
                                            const itemErrors = errors?.[sectionKey]?.[index];
                                            const itemTouched = touched?.[sectionKey]?.[index];
                                            const fieldPath = `${sectionKey}[${index}].${item.key}`;

                                            return (
                                                <React.Fragment key={item.key}>
                                                    {item.kind === FormItemKind.FIELD
                                                        ? renderField(
                                                              item as FormFieldDto,
                                                              itemValue,
                                                              (val) => {
                                                                  const newSectionValues = [...sectionValues];
                                                                  newSectionValues[index] = {
                                                                      ...newSectionValues[index],
                                                                      [item.key]: val,
                                                                  };
                                                                  setFieldValue(sectionKey, newSectionValues);
                                                              },
                                                              sectionInstance,
                                                              itemErrors,
                                                              itemTouched,
                                                              fieldPath,
                                                              setFieldValue,
                                                              () => triggerComputedFieldsUpdate(sectionKey),
                                                          )
                                                        : renderSection(
                                                              item as DynamicSectionDto,
                                                              sectionInstance,
                                                              (nestedKey, val) => {
                                                                  const newSectionValues = [...sectionValues];
                                                                  newSectionValues[index] = {
                                                                      ...newSectionValues[index],
                                                                      [nestedKey]: val,
                                                                  };
                                                                  setFieldValue(sectionKey, newSectionValues);
                                                              },
                                                              itemErrors,
                                                              itemTouched,
                                                          )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </Stack>
                                </Paper>
                            ))}

                            {sectionValues.length < maxItems && (
                                <Button
                                    variant="outlined"
                                    startIcon={<AddOutlined />}
                                    onClick={() => {
                                        const newInstance: any = {};
                                        section.items.forEach((item) => {
                                            if (item.kind === FormItemKind.FIELD) {
                                                const field = item as FormFieldDto;
                                                if (field.type === FormFieldType.LIST) {
                                                    newInstance[item.key] = [];
                                                } else if (field.type === FormFieldType.CHECKBOX) {
                                                    newInstance[item.key] = false;
                                                } else {
                                                    newInstance[item.key] = '';
                                                }
                                            } else if (item.kind === FormItemKind.SECTION) {
                                                const subSection = item as DynamicSectionDto;
                                                if (subSection.repeat.type === RepeatType.REPEATING) {
                                                    newInstance[item.key] = [];
                                                } else {
                                                    newInstance[item.key] = {};
                                                }
                                            }
                                        });
                                        push(newInstance);
                                    }}
                                    sx={{ alignSelf: 'flex-start' }}
                                >
                                    {t('Add {{label}}', { label: section.label })}
                                </Button>
                            )}
                        </Stack>
                    )}
                </FieldArray>
            </Paper>
        );
    };

    const renderFormItem = (
        item: FormFieldDto | DynamicSectionDto,
        values: any,
        setFieldValue: (field: string, value: any) => void,
        errors: any,
        touched: any,
        allFormValues?: any,
        blurKey?: string,
    ) => {
        if (item.kind === FormItemKind.FIELD) {
            return renderField(
                item as FormFieldDto,
                values[item.key],
                (val) => setFieldValue(item.key, val),
                values,
                errors,
                touched,
                undefined,
                setFieldValue,
                () => triggerComputedFieldsUpdate(blurKey ?? item.key), // Sections are updated at once
            );
        } else if (item.kind === FormItemKind.SECTION) {
            return renderSection(item as DynamicSectionDto, values, setFieldValue, errors, touched, allFormValues);
        }
        return null;
    };

    const filterVisibleValues = (schema: FormItemDto[], values: any, allFormValues: any): any => {
        const result: any = {};

        schema.forEach((item) => {
            if (item.kind === FormItemKind.FIELD) {
                result[item.key] = values[item.key];
            } else if (item.kind === FormItemKind.SECTION) {
                const section = item as DynamicSectionDto;

                if (!evaluateVisibility(section.visibleWhen, allFormValues)) {
                    return;
                }

                const sectionValue = values[section.key];

                if (section.repeat.type === RepeatType.REPEATING) {
                    if (Array.isArray(sectionValue)) {
                        result[section.key] = sectionValue.map((instanceValue) =>
                            filterVisibleValues(section.items, instanceValue, allFormValues),
                        );
                    } else {
                        result[section.key] = [];
                    }
                } else {
                    result[section.key] = filterVisibleValues(section.items, sectionValue || {}, allFormValues);
                }
            }
        });

        return result;
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Formik
                initialValues={formValues || initialValues}
                validationSchema={mapFormSchemaToYupSchema(prefilledForm.formSchema, t)}
                onSubmit={(values) => {
                    const filteredValues = filterVisibleValues(prefilledForm.formSchema, values, values);
                    console.debug('setSavedValues', filteredValues);
                    dispatch(setFormValues(filteredValues));
                    dispatch(submitForm(filteredValues));
                }}
                enableReinitialize={!formValues}
                innerRef={formikRef}
            >
                {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                        <Stack spacing={2.5} sx={{ mt: 2 }}>
                            {prefilledForm.formSchema.map((item) => (
                                <React.Fragment key={item.key}>
                                    {renderFormItem(
                                        item as FormFieldDto | DynamicSectionDto,
                                        values,
                                        setFieldValue,
                                        errors,
                                        touched,
                                        values,
                                    )}
                                </React.Fragment>
                            ))}

                            <Button type="submit" variant="contained" sx={{ mt: 3 }}>
                                {t('Submit')}
                            </Button>
                        </Stack>
                    </Form>
                )}
            </Formik>
        </LocalizationProvider>
    );
}

export default DynamicForm;
